import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { api } from '../services/api';
import { CCTV_PURPOSE_OPTIONS } from '../constants/cctv';
import type { Pool } from '../types/domain';

type CctvRow = {
  id: number;
  name: string;
  code: string;
  purpose?: string | null;
  nvr_channel?: number | null;
};
type Item = {
  id: number;
  pool_id: number;
  cctv_id: number;
  cctv_name: string;
  cctv_code: string;
  kind: string;
  status: string;
  starts_at: string;
  ends_at?: string | null;
  created_at?: string | null;
  fetch_mode?: string | null;
  progress?: string | null;
  file_size?: number | null;
  error?: string | null;
};

const STATUS: Record<string, string> = {
  scheduled: '예약',
  recording: '녹화 중',
  completed: '완료',
  failed: '실패',
  cancelled: '취소',
};

function statusLabel(it: { kind?: string; status: string }): string {
  if (it.kind === 'nvr' && it.status === 'recording') return '가져오는 중';
  if (it.kind === 'nvr' && it.status === 'scheduled') return '대기';
  return STATUS[it.status] || it.status;
}

const KIND: Record<string, string> = {
  live: '실시간',
  schedule: '예약',
  nvr: 'NVR',
};

function pad(n: number) {
  return String(n).padStart(2, '0');
}

function fmt(iso?: string | null) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function sizeLabel(n?: number | null) {
  if (!n) return '—';
  if (n < 1024 * 1024) return `${Math.max(1, Math.round(n / 1024))}KB`;
  return `${(n / (1024 * 1024)).toFixed(1)}MB`;
}

function today() {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** 24시간 HH:mm. */
function parseHmTime(raw: string): string | null {
  const s = String(raw || '').trim();
  let m = /^(\d{1,2}):(\d{1,2})$/.exec(s);
  if (m) {
    const h = Number(m[1]);
    const min = Number(m[2]);
    if (h >= 0 && h <= 23 && min >= 0 && min <= 59) return `${pad(h)}:${pad(min)}`;
    return null;
  }
  const digits = s.replace(/\D/g, '');
  if (digits.length === 3 || digits.length === 4) {
    const h = Number(digits.length === 3 ? digits.slice(0, 1) : digits.slice(0, 2));
    const min = Number(digits.length === 3 ? digits.slice(1) : digits.slice(2));
    if (h >= 0 && h <= 23 && min >= 0 && min <= 59) return `${pad(h)}:${pad(min)}`;
  }
  return null;
}

/** 키보드(24시) + 시계 아이콘(네이티브 time 피커). */
function TimeHmField({
  label,
  value,
  onChange,
  placeholder = '09:00',
}: {
  label: string;
  value: string;
  onChange: (hm: string) => void;
  placeholder?: string;
}) {
  const pickerRef = useRef<HTMLInputElement>(null);
  const pickerValue = parseHmTime(value) || '00:00';

  const openPicker = () => {
    const el = pickerRef.current;
    if (!el) return;
    try {
      const show = (el as HTMLInputElement & { showPicker?: () => void }).showPicker;
      if (typeof show === 'function') show.call(el);
      else el.click();
    } catch {
      el.click();
    }
  };

  return (
    <Box sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
      <TextField
        size="small"
        label={label}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={() => {
          const n = parseHmTime(value);
          if (n) onChange(n);
        }}
        InputLabelProps={{ shrink: true }}
        inputProps={{ inputMode: 'numeric', autoComplete: 'off' }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                size="small"
                aria-label={`${label} 시계로 선택`}
                onClick={openPicker}
                edge="end"
              >
                <AccessTimeIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ),
        }}
        sx={{ width: 158 }}
      />
      <input
        ref={pickerRef}
        type="time"
        value={pickerValue}
        onChange={(e) => {
          const v = (e.target.value || '').slice(0, 5);
          if (v) onChange(v);
        }}
        tabIndex={-1}
        aria-hidden
        style={{
          position: 'absolute',
          right: 36,
          width: 1,
          height: 1,
          opacity: 0,
          border: 0,
          padding: 0,
          overflow: 'hidden',
          pointerEvents: 'none',
        }}
      />
    </Box>
  );
}

function errText(e: unknown) {
  const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
  return detail || '요청 실패';
}

type KindKey = 'live' | 'schedule' | 'nvr';

type CaseGroup = {
  key: string;
  items: Item[];
  starts_at: string;
  ends_at?: string | null;
  created_at?: string | null;
};

/** 요청 시각을 초 단위로 맞춤 (배치 내 ms 오차 방지). */
function commandStamp(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

/**
 * 접기 폴더 키.
 * - live: 요청(created_at)만 — 중지 시 ends_at 이 카메라마다 달라져도 한 폴더
 * - schedule: 요청 + 예약 시작 — ends_at 은 종료 시 갱신되므로 키에서 제외
 * - nvr: 요청 + 영상 구간 — 같은 구간 재요청은 별도 폴더
 */
function caseKey(it: Item) {
  const cmd = commandStamp(it.created_at) || `id:${it.id}`;
  if (it.kind === 'nvr') {
    return `${it.kind}|${cmd}|${it.starts_at}|${it.ends_at || ''}`;
  }
  if (it.kind === 'schedule') {
    return `${it.kind}|${cmd}|${it.starts_at}`;
  }
  return `${it.kind}|${cmd}`;
}

function minIso(values: Array<string | null | undefined>): string {
  const times = values
    .filter((v): v is string => Boolean(v))
    .map((v) => ({ v, t: new Date(v).getTime() }))
    .filter((x) => !Number.isNaN(x.t));
  if (!times.length) return '';
  times.sort((a, b) => a.t - b.t);
  return times[0].v;
}

function maxIso(values: Array<string | null | undefined>): string | null {
  const times = values
    .filter((v): v is string => Boolean(v))
    .map((v) => ({ v, t: new Date(v).getTime() }))
    .filter((x) => !Number.isNaN(x.t));
  if (!times.length) return null;
  times.sort((a, b) => b.t - a.t);
  return times[0].v;
}

function caseStatus(list: Item[]): { label: string; color: 'error' | 'primary' | 'default' } {
  const nvr = list.some((it) => it.kind === 'nvr');
  if (list.some((it) => it.status === 'recording')) {
    return { label: nvr ? '가져오는 중' : '녹화 중', color: 'primary' };
  }
  if (list.some((it) => it.status === 'scheduled')) {
    return { label: nvr ? '대기' : '예약', color: 'default' };
  }
  if (list.every((it) => it.status === 'failed')) return { label: '실패', color: 'error' };
  if (list.some((it) => it.status === 'failed')) return { label: '일부 실패', color: 'error' };
  if (list.every((it) => it.status === 'cancelled')) return { label: '취소', color: 'default' };
  if (list.every((it) => it.status === 'completed')) return { label: '완료', color: 'default' };
  return { label: '혼합', color: 'default' };
}

function sumSize(list: Item[]) {
  const sizes = list.map((it) => it.file_size || 0).filter((n) => n > 0);
  if (!sizes.length) return null;
  return sizes.reduce((a, b) => a + b, 0);
}

const LIST_SECTIONS: { kind: KindKey; title: string }[] = [
  { kind: 'live', title: '실시간 녹화 목록' },
  { kind: 'schedule', title: '예약 녹화 목록' },
  { kind: 'nvr', title: 'NVR 가져오기 목록' },
];

const LabelRecordingPanel: React.FC<{ pools: Pool[] }> = ({ pools }) => {
  const [poolId, setPoolId] = useState<number | null>(null);
  const [cctvs, setCctvs] = useState<CctvRow[]>([]);
  const [picked, setPicked] = useState<number[]>([]);
  const [items, setItems] = useState<Item[]>([]);
  /** 종류별 목록에서 체크한 케이스 키 */
  const [selectedByKind, setSelectedByKind] = useState<Record<KindKey, string[]>>({
    live: [],
    schedule: [],
    nvr: [],
  });
  const [openCases, setOpenCases] = useState<Record<string, boolean>>({});
  const [playItem, setPlayItem] = useState<Item | null>(null);
  const [playUrl, setPlayUrl] = useState<string | null>(null);
  const [playLoading, setPlayLoading] = useState(false);
  const [playError, setPlayError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /** 한 번에 하나만: live | schedule | nvr */
  const [actionMode, setActionMode] = useState<'live' | 'schedule' | 'nvr'>('live');
  const [startDate, setStartDate] = useState(today);
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('10:00');
  const [nvrDate, setNvrDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  });
  const [nvrStart, setNvrStart] = useState('09:00');
  const [nvrEnd, setNvrEnd] = useState('09:30');
  const [channels, setChannels] = useState<Record<number, number>>({});
  const [nvrReady, setNvrReady] = useState(false);
  const [nvrHost, setNvrHost] = useState<string | null>(null);
  /** auto=RTSP우선 / trim=세그먼트 download+자르기 */
  const [nvrFetchMode, setNvrFetchMode] = useState<'auto' | 'trim'>('auto');
  const [gatewayOn, setGatewayOn] = useState(false);
  /** '' = 전체 용도 */
  const [purposeFilter, setPurposeFilter] = useState('');
  const seededPool = useRef<number | null>(null);

  const visibleCctvs = useMemo(() => {
    if (!purposeFilter) return cctvs;
    return cctvs.filter((c) => (c.purpose || '관제 모니터링') === purposeFilter);
  }, [cctvs, purposeFilter]);

  const load = useCallback(async (id: number) => {
    const res = await api.get(`/api/label-recordings/pools/${id}`);
    const data = res.data as {
      cctvs: CctvRow[];
      items: Item[];
      nvr_ready?: boolean;
      nvr_host?: string | null;
      site_gateway_enabled?: boolean;
    };
    setCctvs(data.cctvs || []);
    const nextItems = data.items || [];
    setItems(nextItems);
    setSelectedByKind((prev) => {
      const alive = new Set(nextItems.map(caseKey));
      return {
        live: prev.live.filter((key) => alive.has(key)),
        schedule: prev.schedule.filter((key) => alive.has(key)),
        nvr: prev.nvr.filter((key) => alive.has(key)),
      };
    });
    setNvrReady(Boolean(data.nvr_ready));
    setNvrHost(data.nvr_host || null);
    setGatewayOn(Boolean(data.site_gateway_enabled));
    const ch: Record<number, number> = {};
    (data.cctvs || []).forEach((c, i) => {
      ch[c.id] = c.nvr_channel && c.nvr_channel >= 1 ? c.nvr_channel : i + 1;
    });
    setChannels(ch);
    if (seededPool.current !== id) {
      seededPool.current = id;
      setPurposeFilter('');
      setPicked((data.cctvs || []).map((c) => c.id));
    }
  }, []);

  useEffect(() => {
    if (poolId == null && pools.length) {
      const first = Number(pools[0].id);
      if (first) setPoolId(first);
    }
  }, [pools, poolId]);

  const nvrBusy = items.some(
    (it) => it.kind === 'nvr' && (it.status === 'recording' || it.status === 'scheduled')
  );

  useEffect(() => {
    if (poolId == null) return;
    let stop = false;
    const run = () => {
      load(poolId).catch((e) => {
        if (!stop) setError(errText(e));
      });
    };
    run();
    // NVR 진행 중이면 더 자주 갱신(단계·용량 표시)
    const timer = window.setInterval(run, nvrBusy ? 2500 : 5000);
    return () => {
      stop = true;
      window.clearInterval(timer);
    };
  }, [poolId, load, nvrBusy]);

  const recordingNow = items.some((it) => it.kind === 'live' && it.status === 'recording');
  const scheduleActive = items.some(
    (it) => it.kind === 'schedule' && it.status === 'recording'
  );
  const scheduleCancellable = items.some(
    (it) =>
      it.kind === 'schedule' && (it.status === 'recording' || it.status === 'scheduled')
  );
  const nvrCancellable = items.some(
    (it) => it.kind === 'nvr' && (it.status === 'recording' || it.status === 'scheduled')
  );

  useEffect(() => {
    setPicked(visibleCctvs.map((c) => c.id));
  }, [purposeFilter]); // eslint-disable-line react-hooks/exhaustive-deps -- 용도 바뀔 때만 목록 전체 선택

  const toggleAll = (on: boolean) => setPicked(on ? visibleCctvs.map((c) => c.id) : []);

  const toggleOne = (id: number) => {
    setPicked((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      if (poolId != null) await load(poolId);
    } catch (e) {
      setError(errText(e));
    } finally {
      setBusy(false);
    }
  };

  const download = async (item: Item) => {
    setError(null);
    try {
      const res = await api.get(`/api/label-recordings/${item.id}/file`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'video/mp4' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${item.cctv_code || item.cctv_id}_${fmt(item.starts_at).replace(/[: ]/g, '')}.mp4`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(errText(e));
    }
  };

  const itemsByKind = useMemo(() => {
    const out: Record<KindKey, Item[]> = { live: [], schedule: [], nvr: [] };
    for (const it of items) {
      const k = (it.kind === 'schedule' || it.kind === 'nvr' ? it.kind : 'live') as KindKey;
      out[k].push(it);
    }
    return out;
  }, [items]);

  const casesByKind = useMemo(() => {
    const out: Record<KindKey, CaseGroup[]> = { live: [], schedule: [], nvr: [] };
    (Object.keys(out) as KindKey[]).forEach((kind) => {
      const map = new Map<string, Item[]>();
      itemsByKind[kind].forEach((it) => {
        const key = caseKey(it);
        const bucket = map.get(key);
        if (bucket) bucket.push(it);
        else map.set(key, [it]);
      });
      out[kind] = [...map.entries()]
        .map(([key, list]) => {
          const sorted = [...list].sort((a, b) =>
            (a.cctv_name || a.cctv_code).localeCompare(b.cctv_name || b.cctv_code, 'ko'),
          );
          return {
            key,
            items: sorted,
            starts_at: minIso(sorted.map((it) => it.starts_at)) || sorted[0].starts_at,
            ends_at: maxIso(sorted.map((it) => it.ends_at)),
            created_at: minIso(sorted.map((it) => it.created_at)) || sorted[0].created_at || null,
          };
        })
        .sort((a, b) => {
          const ca = a.created_at ? new Date(a.created_at).getTime() : 0;
          const cb = b.created_at ? new Date(b.created_at).getTime() : 0;
          if (cb !== ca) return cb - ca;
          return new Date(b.starts_at).getTime() - new Date(a.starts_at).getTime();
        });
    });
    return out;
  }, [itemsByKind]);

  const toggleListAll = (kind: KindKey, on: boolean) => {
    const keys = casesByKind[kind].map((group) => group.key);
    setSelectedByKind((prev) => ({ ...prev, [kind]: on ? keys : [] }));
  };

  const toggleListOne = (kind: KindKey, key: string) => {
    setSelectedByKind((prev) => {
      const cur = prev[kind];
      return {
        ...prev,
        [kind]: cur.includes(key) ? cur.filter((x) => x !== key) : [...cur, key],
      };
    });
  };

  const deleteIds = (ids: number[], kind?: KindKey) => {
    if (!ids.length) return;
    run(async () => {
      await api.post('/api/label-recordings/bulk-delete', { ids });
      if (kind) setSelectedByKind((prev) => ({ ...prev, [kind]: [] }));
    });
  };

  const deleteSelected = (kind: KindKey) => {
    const keys = new Set(selectedByKind[kind]);
    const ids = casesByKind[kind]
      .filter((group) => keys.has(group.key))
      .flatMap((group) => group.items.map((it) => it.id));
    if (!ids.length) return;
    if (!window.confirm(`선택한 ${keys.size}개 케이스(${ids.length}건)를 삭제?`)) return;
    deleteIds(ids, kind);
  };

  const downloadMany = async (list: Item[]) => {
    const ready = list.filter((it) => it.file_size);
    if (!ready.length) {
      setError('받을 파일 없음');
      return;
    }
    for (const it of ready) {
      await download(it);
    }
  };

  useEffect(() => {
    if (!playItem) {
      setPlayUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
      setPlayError(null);
      setPlayLoading(false);
      return;
    }
    if (!playItem.file_size) {
      setPlayError('아직 파일 없음');
      setPlayLoading(false);
      return;
    }
    let cancelled = false;
    let url: string | null = null;
    setPlayLoading(true);
    setPlayError(null);
    setPlayUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    api
      .get(`/api/label-recordings/${playItem.id}/file`, { responseType: 'blob' })
      .then((res) => {
        if (cancelled) return;
        url = URL.createObjectURL(new Blob([res.data], { type: 'video/mp4' }));
        setPlayUrl(url);
      })
      .catch((e) => {
        if (!cancelled) setPlayError(errText(e));
      })
      .finally(() => {
        if (!cancelled) setPlayLoading(false);
      });
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [playItem]);

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 0.5 }}>
        영상 녹화
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        라벨링용. 지금·예약은 실시간 스트림을 저장. NVR 가져오기는 시설 NVR의 지난 구간(최대 2시간).
        NVR IP·계정은 시설 관리 → 해당 시설 수정의 전송 화질 아래「시설 NVR」에 둠.
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2, alignItems: 'center' }}>
        <FormControl size="small" sx={{ minWidth: 260, maxWidth: 360 }}>
          <InputLabel id="label-rec-pool-label">시설</InputLabel>
          <Select
            labelId="label-rec-pool-label"
            label="시설"
            value={poolId != null ? String(poolId) : ''}
            onChange={(e) => {
              const v = e.target.value;
              setPoolId(v === '' ? null : Number(v));
            }}
            displayEmpty
          >
            {!pools.length && (
              <MenuItem value="" disabled>
                등록된 시설 없음
              </MenuItem>
            )}
            {pools.map((p) => {
              const id = Number(p.id);
              return (
                <MenuItem key={id} value={String(id)}>
                  {p.name}
                  {p.code ? ` (${p.code})` : ''}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel id="label-rec-purpose-label">용도</InputLabel>
          <Select
            labelId="label-rec-purpose-label"
            label="용도"
            value={purposeFilter}
            onChange={(e) => setPurposeFilter(String(e.target.value))}
          >
            <MenuItem value="">전체</MenuItem>
            {CCTV_PURPOSE_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            {actionMode === 'nvr'
              ? `가져올 CCTV 선택 (${picked.length}/${visibleCctvs.length}). 한 대만 고르면 채널별 테스트에 편함.`
              : actionMode === 'schedule'
                ? `예약할 CCTV 선택 (${picked.length}/${visibleCctvs.length})`
                : `녹화할 CCTV 선택 (${picked.length}/${visibleCctvs.length})`}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1, maxHeight: 140, overflow: 'auto' }}>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={visibleCctvs.length > 0 && picked.length === visibleCctvs.length}
                  indeterminate={picked.length > 0 && picked.length < visibleCctvs.length}
                  onChange={(e) => toggleAll(e.target.checked)}
                />
              }
              label="전체"
            />
            {visibleCctvs.map((c) => (
              <FormControlLabel
                key={c.id}
                control={
                  <Checkbox size="small" checked={picked.includes(c.id)} onChange={() => toggleOne(c.id)} />
                }
                label={c.name || c.code}
              />
            ))}
            {!visibleCctvs.length && (
              <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>
                이 용도의 카메라 없음
              </Typography>
            )}
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            아래 버튼으로 작업·목록을 전환. 선택한 종류의 녹화만 보입니다.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
            <Button
              variant={actionMode === 'live' ? 'contained' : 'outlined'}
              onClick={() => setActionMode('live')}
            >
              지금 녹화
              {casesByKind.live.length > 0 ? ` (${casesByKind.live.length})` : ''}
            </Button>
            <Button
              variant={actionMode === 'schedule' ? 'contained' : 'outlined'}
              onClick={() => setActionMode('schedule')}
            >
              예약
              {casesByKind.schedule.length > 0 ? ` (${casesByKind.schedule.length})` : ''}
            </Button>
            <Button
              variant={actionMode === 'nvr' ? 'contained' : 'outlined'}
              onClick={() => setActionMode('nvr')}
            >
              NVR 가져오기
              {casesByKind.nvr.length > 0 ? ` (${casesByKind.nvr.length})` : ''}
            </Button>
          </Box>
          {actionMode === 'live' && (
            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary" sx={{ width: '100%' }}>
                선택한 카메라를 바로 녹화. 종료하지 않으면 8시간 뒤 끝.
                종료=파일 보관, 취소=파일 삭제.
              </Typography>
              <Button
                variant="contained"
                disabled={busy || !poolId || picked.length === 0}
                onClick={() =>
                  run(() => api.post('/api/label-recordings/live', { pool_id: poolId, cctv_ids: picked }))
                }
              >
                녹화 시작
              </Button>
              <Button
                variant="outlined"
                color="primary"
                disabled={busy || !poolId || !recordingNow}
                onClick={() =>
                  run(() =>
                    api.post('/api/label-recordings/stop-live', {
                      pool_id: poolId,
                      kind: 'live',
                    })
                  )
                }
              >
                녹화 종료
              </Button>
              <Button
                variant="outlined"
                color="inherit"
                disabled={busy || !poolId || !recordingNow}
                onClick={() =>
                  run(() =>
                    api.post('/api/label-recordings/cancel-live', {
                      pool_id: poolId,
                      kind: 'live',
                    })
                  )
                }
              >
                녹화 취소
              </Button>
            </Box>
          )}
          {actionMode === 'schedule' && (
            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary" sx={{ width: '100%' }}>
                시작 날짜·시작–종료 시각. 종료가 더 이르면 다음 날. 한 구간 최대 8시간.
                종료 시각에 자동 보관. 종료=지금 끊고 보관, 취소=삭제.
              </Typography>
              <TextField
                size="small"
                type="date"
                label="시작 날짜"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <TimeHmField
                label="시작 (24시)"
                placeholder="09:00"
                value={startTime}
                onChange={setStartTime}
              />
              <TimeHmField
                label="종료 (24시)"
                placeholder="10:00"
                value={endTime}
                onChange={setEndTime}
              />
              <Button
                variant="contained"
                disabled={busy || !poolId || picked.length === 0}
                onClick={() =>
                  run(async () => {
                    const st = parseHmTime(startTime);
                    const et = parseHmTime(endTime);
                    if (!st || !et) {
                      setError('시각은 24시간제 HH:mm (예: 09:00, 1430)');
                      return;
                    }
                    setStartTime(st);
                    setEndTime(et);
                    await api.post('/api/label-recordings/schedules', {
                      pool_id: poolId,
                      cctv_ids: picked,
                      start_date: startDate,
                      start_time: st,
                      end_time: et,
                    });
                  })
                }
              >
                예약 저장
              </Button>
              <Button
                variant="outlined"
                color="primary"
                disabled={busy || !poolId || !scheduleActive}
                onClick={() =>
                  run(() =>
                    api.post('/api/label-recordings/stop-live', {
                      pool_id: poolId,
                      kind: 'schedule',
                    })
                  )
                }
              >
                녹화 종료
              </Button>
              <Button
                variant="outlined"
                color="inherit"
                disabled={busy || !poolId || !scheduleCancellable}
                onClick={() =>
                  run(() =>
                    api.post('/api/label-recordings/cancel-live', {
                      pool_id: poolId,
                      kind: 'schedule',
                    })
                  )
                }
              >
                예약/녹화 취소
              </Button>
            </Box>
          )}
          {actionMode === 'nvr' && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1, whiteSpace: 'pre-line' }}>
                {`지난 구간만 가능(최대 2시간). 위 체크한 CCTV만 가져옴(선택 ${picked.length}대).
현장 게이트웨이가 NVR에서 받아 중앙에 올림.
${!gatewayOn ? '· 시설의 현장 게이트웨이가 꺼져 있음 → 시설 설정에서 켠 뒤 페어링\n' : ''}${
                  !nvrReady
                    ? `· NVR IP·계정이 없음 → 시설 수정「시설 NVR」에 입력${nvrHost ? ` (호스트: ${nvrHost})` : ''}\n`
                    : `· NVR: ${nvrHost || '—'}\n`
                }· 아래 채널 번호를 NVR 화면과 같게 맞춤
· 자동: ch1은 RTSP 우선, ch2+는 재생 RTSP가 기종에 따라 바로 안 되어 짧게 확인 후 download/trim
· 여러 채널·빠른 확정이면「세그먼트 download 후 trim」권장`}
              </Typography>
              <FormControl component="fieldset" sx={{ mb: 1, width: '100%' }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  가져오기 방식
                </Typography>
                <RadioGroup
                  row
                  value={nvrFetchMode}
                  onChange={(e) => setNvrFetchMode(e.target.value === 'trim' ? 'trim' : 'auto')}
                >
                  <FormControlLabel
                    value="auto"
                    control={<Radio size="small" />}
                    label="자동(권장): RTSP 구간 재생 → 실패 시 download+trim"
                  />
                  <FormControlLabel
                    value="trim"
                    control={<Radio size="small" />}
                    label="세그먼트 download 후 trim"
                  />
                </RadioGroup>
              </FormControl>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 1 }}>
                <TextField
                  size="small"
                  type="date"
                  label="날짜"
                  value={nvrDate}
                  onChange={(e) => setNvrDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
                <TimeHmField
                  label="시작 (24시)"
                  placeholder="09:00"
                  value={nvrStart}
                  onChange={setNvrStart}
                />
                <TimeHmField
                  label="종료 (24시)"
                  placeholder="09:30"
                  value={nvrEnd}
                  onChange={setNvrEnd}
                />
                <Button
                  variant="contained"
                  disabled={busy || !poolId || picked.length === 0 || !nvrReady || !gatewayOn}
                  onClick={() =>
                    run(async () => {
                      const st = parseHmTime(nvrStart);
                      const et = parseHmTime(nvrEnd);
                      if (!st || !et) {
                        setError('시각은 24시간제 HH:mm (예: 09:00, 1430)');
                        return;
                      }
                      setNvrStart(st);
                      setNvrEnd(et);
                      const ch: Record<string, number> = {};
                      picked.forEach((id) => {
                        ch[String(id)] = channels[id] || 1;
                      });
                      await api.post('/api/label-recordings/nvr-import', {
                        pool_id: poolId,
                        cctv_ids: picked,
                        start_date: nvrDate,
                        start_time: st,
                        end_time: et,
                        channels: ch,
                        fetch_mode: nvrFetchMode,
                      });
                    })
                  }
                >
                  가져오기 요청
                </Button>
                <Button
                  variant="outlined"
                  color="warning"
                  disabled={busy || !poolId || !nvrCancellable}
                  onClick={() => {
                    if (!window.confirm('이 시설의 NVR 가져오기(대기·진행)를 모두 중단?')) return;
                    run(() =>
                      api.post('/api/label-recordings/nvr-cancel', { pool_id: poolId })
                    );
                  }}
                >
                  전체 중단
                </Button>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {picked.map((id) => {
                  const c = cctvs.find((x) => x.id === id);
                  return (
                    <TextField
                      key={id}
                      size="small"
                      type="number"
                      label={`${c?.name || c?.code || id} 채널`}
                      value={channels[id] ?? 1}
                      onChange={(e) =>
                        setChannels((prev) => ({
                          ...prev,
                          [id]: Math.max(1, parseInt(e.target.value, 10) || 1),
                        }))
                      }
                      inputProps={{ min: 1, max: 256 }}
                      sx={{ width: 140 }}
                    />
                  );
                })}
              </Box>
            </Box>
          )}
          {(() => {
            const section = LIST_SECTIONS.find((s) => s.kind === actionMode) || LIST_SECTIONS[0];
            const kind = section.kind;
            const title = section.title;
            const groups = casesByKind[kind];
            const selected = selectedByKind[kind];
            const allOn = groups.length > 0 && selected.length === groups.length;
            const someOn = selected.length > 0 && selected.length < groups.length;
            return (
              <Paper key={kind} variant="outlined" sx={{ mb: 2 }}>
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: 1,
                    px: 1.5,
                    py: 1,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
                    {title}
                    <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      {groups.length}건
                    </Typography>
                  </Typography>
                  <Button
                    size="small"
                    color="error"
                    variant="outlined"
                    startIcon={<DeleteOutlineIcon />}
                    disabled={busy || selected.length === 0}
                    onClick={() => deleteSelected(kind)}
                  >
                    선택 삭제{selected.length > 0 ? ` (${selected.length})` : ''}
                  </Button>
                </Box>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          size="small"
                          checked={allOn}
                          indeterminate={someOn}
                          disabled={!groups.length}
                          onChange={(e) => toggleListAll(kind, e.target.checked)}
                          inputProps={{ 'aria-label': `${title} 전체 선택` }}
                        />
                      </TableCell>
                      <TableCell>케이스</TableCell>
                      <TableCell>요청</TableCell>
                      <TableCell>영상 시작</TableCell>
                      <TableCell>영상 종료</TableCell>
                      <TableCell>상태</TableCell>
                      <TableCell>크기</TableCell>
                      <TableCell align="right" />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {groups.map((group) => {
                      const open = Boolean(openCases[group.key]);
                      const status = caseStatus(group.items);
                      const readyCount = group.items.filter((it) => it.file_size).length;
                      const caseActive =
                        kind === 'nvr' &&
                        group.items.some(
                          (it) => it.status === 'recording' || it.status === 'scheduled'
                        );
                      return (
                        <React.Fragment key={group.key}>
                          <TableRow
                            hover
                            selected={selected.includes(group.key)}
                            onClick={() =>
                              setOpenCases((prev) => ({ ...prev, [group.key]: !prev[group.key] }))
                            }
                            sx={{ cursor: 'pointer' }}
                          >
                            <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                              <Checkbox
                                size="small"
                                checked={selected.includes(group.key)}
                                onChange={() => toggleListOne(kind, group.key)}
                              />
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                {open ? (
                                  <ExpandMoreIcon fontSize="small" />
                                ) : (
                                  <ChevronRightIcon fontSize="small" />
                                )}
                                카메라 {group.items.length}대
                              </Box>
                            </TableCell>
                            <TableCell>{fmt(group.created_at)}</TableCell>
                            <TableCell>{fmt(group.starts_at)}</TableCell>
                            <TableCell>{fmt(group.ends_at)}</TableCell>
                            <TableCell>
                              <Chip size="small" label={status.label} color={status.color} />
                              {kind === 'nvr' &&
                              group.items.some((it) => it.status === 'recording') ? (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  display="block"
                                  sx={{ mt: 0.25, maxWidth: 280, whiteSpace: 'normal' }}
                                >
                                  {group.items.find((it) => it.status === 'recording' && it.progress)
                                    ?.progress || '현장 처리 중'}
                                </Typography>
                              ) : null}
                            </TableCell>
                            <TableCell>{sizeLabel(sumSize(group.items))}</TableCell>
                            <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                              {caseActive && (
                                <Button
                                  size="small"
                                  color="warning"
                                  sx={{ mr: 0.5 }}
                                  disabled={busy || !poolId}
                                  onClick={() => {
                                    if (
                                      !window.confirm(
                                        `이 케이스 NVR 가져오기 ${group.items.length}건을 중단?`
                                      )
                                    ) {
                                      return;
                                    }
                                    run(() =>
                                      api.post('/api/label-recordings/nvr-cancel', {
                                        pool_id: poolId,
                                        ids: group.items.map((it) => it.id),
                                      })
                                    );
                                  }}
                                >
                                  중단
                                </Button>
                              )}
                              <IconButton
                                size="small"
                                aria-label="케이스 받기"
                                disabled={!readyCount}
                                onClick={() => void downloadMany(group.items)}
                              >
                                <DownloadIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                aria-label="케이스 삭제"
                                onClick={() => {
                                  if (!window.confirm(`이 케이스의 녹화 ${group.items.length}건을 삭제?`)) return;
                                  deleteIds(group.items.map((it) => it.id));
                                }}
                              >
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell colSpan={8} sx={{ py: 0, borderBottom: open ? undefined : 0 }}>
                              <Collapse in={open} timeout="auto" unmountOnExit>
                                <Table size="small" sx={{ my: 1, bgcolor: 'action.hover' }}>
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>카메라</TableCell>
                                      <TableCell>상태</TableCell>
                                      <TableCell>크기</TableCell>
                                      <TableCell align="right" />
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {group.items.map((it) => (
                                      <TableRow
                                        key={it.id}
                                        hover
                                        onClick={() => {
                                          if (!it.file_size) {
                                            setError('아직 재생할 파일 없음');
                                            return;
                                          }
                                          setPlayItem(it);
                                        }}
                                        sx={{ cursor: 'pointer' }}
                                      >
                                        <TableCell>
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <PlayArrowIcon fontSize="small" color={it.file_size ? 'primary' : 'disabled'} />
                                            {it.cctv_name || it.cctv_code}
                                          </Box>
                                        </TableCell>
                                        <TableCell>
                                          <Chip
                                            size="small"
                                            label={statusLabel(it)}
                                            color={
                                              it.status === 'failed'
                                                ? 'error'
                                                : it.status === 'recording'
                                                  ? 'primary'
                                                  : 'default'
                                            }
                                          />
                                          {it.kind === 'nvr' && it.progress ? (
                                            <Typography
                                              variant="caption"
                                              color="text.secondary"
                                              display="block"
                                              sx={{ mt: 0.25, maxWidth: 360, whiteSpace: 'normal' }}
                                            >
                                              {it.progress}
                                            </Typography>
                                          ) : null}
                                          {it.kind === 'nvr' && it.fetch_mode && (it.status === 'recording' || it.status === 'scheduled') ? (
                                            <Typography variant="caption" color="text.disabled" display="block">
                                              방식: {it.fetch_mode === 'trim' ? 'download+trim' : '자동'}
                                            </Typography>
                                          ) : null}
                                          {it.error ? (
                                            <Typography variant="caption" color="error" display="block">
                                              {it.error}
                                            </Typography>
                                          ) : null}
                                        </TableCell>
                                        <TableCell>{sizeLabel(it.file_size)}</TableCell>
                                        <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                                          <IconButton
                                            size="small"
                                            aria-label="받기"
                                            disabled={!it.file_size}
                                            onClick={() => void download(it)}
                                          >
                                            <DownloadIcon fontSize="small" />
                                          </IconButton>
                                          <IconButton
                                            size="small"
                                            aria-label="삭제"
                                            onClick={() => {
                                              if (!window.confirm('이 녹화를 삭제?')) return;
                                              run(() => api.delete(`/api/label-recordings/${it.id}`));
                                            }}
                                          >
                                            <DeleteOutlineIcon fontSize="small" />
                                          </IconButton>
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                      );
                    })}
                    {!groups.length && (
                      <TableRow>
                        <TableCell colSpan={8}>
                          <Typography variant="body2" color="text.secondary">
                            이 목록에 녹화 없음
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Paper>
            );
          })()}
      </Box>
      <Dialog
        open={playItem != null}
        onClose={() => setPlayItem(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {playItem ? `${playItem.cctv_name || playItem.cctv_code} · ${fmt(playItem.starts_at)}` : '재생'}
        </DialogTitle>
        <DialogContent>
          {playLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}
          {playError && (
            <Alert severity="error" sx={{ mb: 1 }}>
              {playError}
            </Alert>
          )}
          {playUrl && (
            <video
              key={playUrl}
              src={playUrl}
              controls
              autoPlay
              style={{ width: '100%', maxHeight: '70vh', background: '#000' }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setPlayItem(null)}>
            닫기
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LabelRecordingPanel;
